'use client';

import { useRef, useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Bar } from '@visx/shape';
import { max, extent, bisector, Bin, format, bin } from 'd3';
import * as d3 from 'd3';
import { Series } from 'mode-shared/hooks/synth/usePricePredictions';
import { CHART_CONFIG } from 'mode-shared/utils/graphs';
import { useResponsiveDimensions } from 'mode-shared/hooks/useResponsiveDimensions';

const DIMENSIONS = {
  margin: { left: 26, bottom: 40, top: 0, right: 4 },
};

const formatPrice = (value: number, compact = true) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    notation: compact ? 'compact' : 'standard',
    minimumSignificantDigits: 3,
    maximumSignificantDigits: 8,
  }).format(value);
};

export const PriceDistribution = ({ series, selectedTime }: { series: Series[]; selectedTime?: Date }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResponsiveDimensions({
    defaultWidth: 360,
    defaultHeight: 400,
    desiredRatio: 0.7,
    breakpoint: 1024,
  });

  const config = useMemo(() => ({ ...CHART_CONFIG, ...DIMENSIONS, width, height }), [width, height]);
  const innerWidth = config.width - config.margin.left - config.margin.right;
  const innerHeight = config.height - config.margin.top - config.margin.bottom;
  const prices = useMemo(() => {
    if (!series || !selectedTime) return [];

    const bisect = bisector<{ time: Date }, Date>((d) => d.time).left;
    return series
      .map((s) => {
        const idx = bisect(s.values, selectedTime);
        if (idx >= s.values.length) return null;
        const d0 = s.values[idx - 1];
        const d1 = s.values[idx];
        if (!d0 || !d1) return null;
        return d0.time.getTime() - selectedTime.getTime() > selectedTime.getTime() - d1.time.getTime()
          ? d1.price
          : d0.price;
      })
      .filter((p): p is number => p !== null);
  }, [series, selectedTime]);

  const bins = useMemo(() => {
    if (prices.length === 0) return [];
    const h = bin();
    h.domain(extent(prices) as [number, number]);
    h.thresholds(25);
    return h(prices);
  }, [prices]);

  const scales = useMemo(() => {
    if (bins.length === 0) return null;
    return {
      x: scaleLinear({
        domain: [bins[0].x0 || 0, bins[bins.length - 1].x1 || 0],
        range: [0, innerWidth],
      }),
      y: scaleLinear({
        domain: [0, max(bins, (d) => d.length) || 0],
        range: [innerHeight, 0],
      }),
    };
  }, [bins, innerWidth, innerHeight]);

  const handleBarHover = (event: React.MouseEvent, bin: Bin<number, number>): void => {
    if (!tooltipRef.current) return;

    tooltipRef.current.style.opacity = '1';
    tooltipRef.current.innerHTML = `
      <div class="tooltip-content">
        <div>${formatPrice(bin.x0 || 0, false)} - ${formatPrice(bin.x1 || 0, false)}</div>
        <div>Count: ${bin.length}</div>
      </div>
    `;
    tooltipRef.current.style.left = `${event.nativeEvent.offsetX}px`;
    tooltipRef.current.style.top = `${event.nativeEvent.offsetY}px`;
  };

  if (!scales?.x || !scales?.y || bins.length === 0) {
    return (
      <div className="flex items-center justify-center w-full md:w-[360px] shrink-0" style={{ height: config.height }}>
        <p className="text-neutral-500 text-sm font-medium">Select the timeline</p>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <svg width={config.width} height={config.height}>
        <Group left={config.margin.left} top={config.margin.top}>
          <GridRows
            scale={scales.y}
            width={innerWidth}
            height={innerHeight}
            stroke={config.colors.grid}
            opacity={config.gridRows.opacity}
            strokeWidth={config.gridRows.strokeWidth}
          />
          {bins.map((bin, i) => (
            <Bar
              key={i}
              x={scales.x(bin.x0 || 0)}
              y={scales.y(bin.length)}
              width={Math.max(0, scales.x(bin.x1 || 0) - scales.x(bin.x0 || 0) - 1)}
              height={innerHeight - scales.y(bin.length)}
              fill={config.colors.primary}
              onMouseEnter={(e) => handleBarHover(e, bin)}
              onMouseLeave={() => {
                if (tooltipRef.current) {
                  tooltipRef.current.style.opacity = '0';
                }
              }}
            />
          ))}
          <AxisLeft
            scale={scales.y}
            stroke={config.colors.axis}
            tickStroke={config.colors.axis}
            numTicks={12}
            tickLabelProps={() => ({
              fill: config.colors.axis,
              fontSize: 11,
              textAnchor: 'end',
              dx: -4,
            })}
          />
          <AxisBottom
            top={innerHeight}
            scale={scales.x}
            stroke={config.colors.axis}
            tickStroke={config.colors.axis}
            numTicks={7}
            tickFormat={(d) => formatPrice(Number(d))}
            tickLabelProps={() => ({
              fill: config.colors.axis,
              fontSize: 11,
              textAnchor: 'middle',
              dy: 4,
            })}
          />
        </Group>
      </svg>
      <div
        ref={tooltipRef}
        className={`
          text-xs
          absolute 
          opacity-0
          bg-grey-900
          p-2
          rounded
          text-neutral-400
          pointer-events-none
          transition-opacity duration-200
        `}
      />
    </div>
  );
};
